import { createTagFilterRule } from "../../../companies/tags/filter_rules";
// convex/lib/config/domains/sales.ts

import { saleValidator } from "../../../companies/sales";
import { validatorSaleItem } from "../../../companies/sales/items";
import { validatorSeller } from "../../../companies/sales/sellers";
import { sellersInternalControlStateRule } from "../../../companies/sales/sellers/filter_rules";
import { validatorClient } from "../../../companies/clients";
import { validatorProductService } from "../../../companies/products";
import { validatorInvoice } from "../../../companies/invoices";
import { validatorUsers } from "../../../system/users";
import type { DomainGroup, TableScope } from "../types";

// ============================================================================
// SALES DOMAIN - Status Values for i18n
// ============================================================================

export const SALE_STATUSES = [
  "draft",
  "confirmed",
  "invoiced",
  "cancelled",
] as const;
export const SELLER_STATUSES = ["active", "inactive"] as const;

export const salesDomain: DomainGroup = {
  id: "sales",
  name: "Ventas",
  description: "Gestión de ventas y vendedores",
  icon: "ShoppingCart",
  order: 6,
  status: "active",

  // ── Configuración i18n del dominio ────────────────────
  i18n: {
    namespace: "sales",
    keys: {
      title: "sales.title",
      description: "sales.description",
    },
  },

  modules: [
    {
      id: "sales",
      name: "Ventas",
      dashboardAccess: "elevated",
      description: "Registro y gestión de ventas a clientes",
      icon: "ShoppingCart",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/sales"],
      navigation: {
        showInNav: true,
        contexts: ["main"],
        navOrder: 5,
        status: "active",
      },

      // ── Configuración i18n del módulo ────────────────────
      i18n: {
        namespace: "sales.sales",
        keys: {
          title: "sales.sales.title",
          description: "sales.sales.description",
          longDescription: "sales.sales.longDescription",
          searchPlaceholder: "sales.sales.searchPlaceholder",
          emptyState: "sales.sales.emptyState",
          actions: {
            create: "sales.sales.actions.create",
            edit: "sales.sales.actions.edit",
            delete: "sales.sales.actions.delete",
            view: "sales.sales.actions.view",
          },
        },
        translatableFields: ["notes"],
        translatableEnums: [{ field: "status", values: SALE_STATUSES }],
      },

      tables: [
        {
          tableName: "sales",
          schema: saleValidator,
          scope: "branch" as TableScope,
          relations: [
            {
              key: "seller",
              schema: validatorSeller,
              tableName: "sellers",
            },
            {
              key: "client",
              schema: validatorClient,
              tableName: "clients",
              searchableFields: ["name", "contactPerson"],
            },
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "invoice",
              sourceField: "invoiceId",
              schema: validatorInvoice,
              tableName: "invoices",
            },
          ],
          searchFields: ["code", "notes"],
          hasStatusIndex: false,
          customRules: [createTagFilterRule("sales")],
          withTags: true,
        },
        {
          tableName: "sales_items",
          schema: validatorSaleItem,
          scope: "company" as TableScope,
          relations: [
            {
              key: "sale",
              schema: saleValidator,
              tableName: "sales",
            },
            {
              key: "product",
              schema: validatorProductService,
              tableName: "products",
            },
          ],
          searchFields: ["description"],
          hasStatusIndex: false,
          customRules: [],
        },
      ],
      readOnlyTables: [
        { tableName: "clients" },
        { tableName: "sellers" },
        { tableName: "products" },
      ],
    },
    {
      id: "sellers",
      name: "Vendedores",
      description: "Gestión de vendedores y equipo de ventas",
      icon: "Users",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/sales/sellers"],
      navigation: {
        showInNav: true,
        contexts: ["main"],
        navOrder: 6,
        status: "active",
      },

      // ── Configuración i18n del módulo ────────────────────
      i18n: {
        namespace: "sales.sellers",
        keys: {
          title: "sales.sellers.title",
          description: "sales.sellers.description",
          longDescription: "sales.sellers.longDescription",
          searchPlaceholder: "sales.sellers.searchPlaceholder",
          emptyState: "sales.sellers.emptyState",
          actions: {
            create: "sales.sellers.actions.create",
            edit: "sales.sellers.actions.edit",
            delete: "sales.sellers.actions.delete",
            view: "sales.sellers.actions.view",
          },
        },
        translatableFields: [],
        translatableEnums: [{ field: "status", values: SELLER_STATUSES }],
      },

      tables: [
        {
          tableName: "sellers",
          schema: validatorSeller,
          scope: "company" as TableScope,
          masterData: true,
          relations: [
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["email", "phone", "fullName"],
          hasStatusIndex: true,
          customRules: [sellersInternalControlStateRule],
        },
      ],
      readOnlyTables: [],
    },
  ],
};
